import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isAuthenticated, clearToken, resolveImageUrl } from "@/lib/api";

const navLinks = [
  { label: "회사 소개", href: "#about" },
  { label: "장비 정보", href: "#equipment" },
  { label: "서비스", href: "#services" },
  { label: "작업 이미지", href: "#gallery" },
  { label: "문의 게시판", href: "/inquiries" },
];

const adminLinks = [
  { label: "대시보드", href: "/admin/dashboard" },
  { label: "장비 관리", href: "/admin/equipment" },
  { label: "갤러리 관리", href: "/admin/gallery" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [mobileAdminOpen, setMobileAdminOpen] = useState(false);
  const [session, setSession] = useState(false);
  const adminRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin") || location.pathname === "/inquiries";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSession(isAuthenticated());
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (adminRef.current && !adminRef.current.contains(e.target as Node)) {
        setAdminOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (href: string) => {
    setOpen(false);
    setMobileAdminOpen(false);
    if (href.startsWith("#")) {
      if (location.pathname === "/") {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate("/" + href);
      }
    } else {
      navigate(href);
    }
  };

  const handleLogout = () => {
    setAdminOpen(false);
    setMobileAdminOpen(false);
    clearToken();
    setSession(false);
    navigate("/");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        isAdminPage
          ? "bg-foreground-950 border-b border-foreground-800"
          : scrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/80 shadow-sm"
            : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === "/") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img
              src={resolveImageUrl("/uploads/logo.webp")}
              alt="우공이산 로고"
              className="h-10 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(l.href);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  isAdminPage
                    ? "text-background-100/80 hover:text-background-50 hover:bg-foreground-800"
                    : scrolled
                      ? "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
                      : "text-background-50 hover:text-accent-300"
                }`}
              >
                {l.label}
              </a>
            ))}

            {/* Desktop Admin Dropdown */}
            {session && (
              <div className="relative" ref={adminRef}>
                <button
                  onClick={() => setAdminOpen((s) => !s)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                    isAdminPage
                      ? "text-background-100/80 hover:text-background-50 hover:bg-foreground-800"
                      : scrolled
                        ? "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
                        : "text-background-50 hover:text-accent-300"
                  }`}
                >
                  관리
                  <i
                    className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${
                      adminOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {adminOpen && (
                  <div className="absolute top-full right-0 mt-1 w-44 bg-background-50 rounded-lg border border-background-200/80 shadow-lg py-1 z-50">
                    {adminLinks.map((l) => (
                      <a
                        key={l.href}
                        href={l.href}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(l.href);
                          setAdminOpen(false);
                        }}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        {l.label}
                      </a>
                    ))}
                    <div className="border-t border-background-200/60 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
                    >
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {!session && (
              <button
                onClick={() => navigate("/auth/login")}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isAdminPage
                    ? "text-background-100/80 hover:text-background-50"
                    : scrolled
                      ? "text-foreground-500 hover:text-primary-700"
                      : "text-background-50/80 hover:text-accent-300"
                }`}
              >
                <i className="ri-shield-user-line w-4 h-4 flex items-center justify-center"></i>
                <span>관리자 로그인</span>
              </button>
            )}
            <a
              href="tel:010-8079-0428"
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                isAdminPage ? "text-background-100/80" : scrolled ? "text-foreground-700" : "text-background-50"
              }`}
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
              <span className="whitespace-nowrap">010-8079-0428</span>
            </a>
            <button
              onClick={() => navigate("/inquiries")}
              className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              상담 문의
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setOpen((s) => !s)}
            className={`lg:hidden w-10 h-10 rounded-md flex items-center justify-center cursor-pointer ${
              isAdminPage ? "text-background-50" : scrolled ? "text-foreground-900" : "text-background-50"
            }`}
            aria-label="메뉴"
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {open && (
          <div className="lg:hidden pb-6 border-t border-background-200/60 bg-background-50 -mx-6 md:-mx-10 px-6 md:px-10">
            <div className="flex flex-col gap-1 pt-3">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(l.href);
                  }}
                  className="px-4 py-3 rounded-md text-foreground-800 hover:bg-primary-50 hover:text-primary-700 font-medium cursor-pointer"
                >
                  {l.label}
                </a>
              ))}

              {/* Mobile Admin Section */}
              {session && (
                <>
                  <div className="border-t border-background-200/60 my-1"></div>
                  <button
                    onClick={() => setMobileAdminOpen((s) => !s)}
                    className="px-4 py-3 rounded-md text-foreground-800 hover:bg-primary-50 hover:text-primary-700 font-medium cursor-pointer flex items-center justify-between"
                  >
                    <span>관리</span>
                    <i
                      className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform duration-200 ${
                        mobileAdminOpen ? "rotate-180" : ""
                      }`}
                    ></i>
                  </button>
                  {mobileAdminOpen && (
                    <div className="ml-2 border-l-2 border-background-200/60 pl-3">
                      {adminLinks.map((l) => (
                        <a
                          key={l.href}
                          href={l.href}
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavClick(l.href);
                          }}
                          className="block px-4 py-2.5 rounded-md text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          {l.label}
                        </a>
                      ))}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2.5 rounded-md text-sm text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        로그아웃
                      </button>
                    </div>
                  )}
                </>
              )}

              {!session && (
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/auth/login");
                  }}
                  className="px-4 py-3 rounded-md text-foreground-800 hover:bg-primary-50 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-shield-user-line w-5 h-5 flex items-center justify-center"></i>
                  <span>관리자 로그인</span>
                </button>
              )}

              <button
                onClick={() => navigate("/inquiries")}
                className="mt-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 font-medium cursor-pointer whitespace-nowrap"
              >
                상담 문의
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}